import React, { useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import { useDispatch, useSelector } from 'react-redux';
import Isvg from 'react-inlinesvg';

import { VenueUserFeedbackOptionType } from '@classpass/protobuf-definitions/lib/web/models/partners/reservation';
import classNames from 'classnames';

import { bem } from 'helpers/class-name';
import Segment from 'helpers/segment';

import { ICONS } from 'constants/icons';
import { intlMessage } from 'constants/propTypes';
import Button from 'components/Button';
import Callout from 'components/Callout';
import { CardSection } from 'components/Card';
import { openDecagonChat } from 'components/Decagon';
import { Form } from 'components/HookForm';
import { closeModal } from 'components/Modal';
import Message from 'components/Message';
import When from 'components/When';
import { refreshAppointmentsAfterReservationFeedback } from 'features/Appointments/Appointments.saga';
import { refreshRosterAfterReservationFeedback } from 'features/YieldManagement/YieldManagement.saga';
import { selectIsMobile } from 'selectors/ui';

import { submitReservationFeedback } from '../ReservationFeedback.helpers';
import {
  PROCESSING_MIN_DISPLAY_MS,
  REPORT_TYPE_DISRUPTIVE,
  REPORT_TYPES,
  REPORT_TYPE_TRIAL,
} from '../ReservationFeedbackModalV2.constants';
import GuidancePanel from './GuidancePanel';
import ReportFormPanel from './ReportFormPanel';
import ReportDetailsHeader from './ReportDetailsHeader';

import './ReportDetails.scss';
import messages from '../ReservationFeedbackModalV2.messages';

const { block, element } = bem('report-details');
const FORM_NAME = 'reservation-feedback-report-form';

const DISRUPTIVE_REASON_MESSAGE_TO_VALUE = {
  [messages.disruptiveReasonNotFollowingPolicy.id]: VenueUserFeedbackOptionType.NOT_FOLLOWING_POLICY,
  [messages.disruptiveReasonRepeatedlyLate.id]: VenueUserFeedbackOptionType.LATE,
  [messages.disruptiveReasonRudeToStaff.id]: VenueUserFeedbackOptionType.RUDE,
};

function buildFeedbackAndDescription(formValues, reportType) {
  const description = (formValues?.additionalDetails ?? '').trim();
  let feedback;
  if (reportType === REPORT_TYPE_TRIAL) {
    feedback = [VenueUserFeedbackOptionType.TRIAL_ABUSE];
  } else if (reportType === REPORT_TYPE_DISRUPTIVE) {
    const reason = formValues?.disruptiveReason;
    const reasonId = reason?.id && DISRUPTIVE_REASON_MESSAGE_TO_VALUE[reason.id];
    feedback = [VenueUserFeedbackOptionType.DISRUPTIVE_USER, reasonId].filter(Boolean);
  } else {
    feedback = [];
  }
  return { description, feedback };
}

const ReportDetails = ({
  fullName,
  profileUrl,
  reasonType,
  guidelines,
  venueId,
  reservationId,
  scheduleId,
  reportType,
  setProcessingStep,
  setSuccessStep,
  resizeModal,
  showSubmitError,
  onSubmitError,
}) => {
  const dispatch = useDispatch();
  const isMobile = useSelector(selectIsMobile);
  const [showGuidancePanel, setShowGuidancePanel] = useState(false);

  useEffect(() => {
    if (resizeModal && typeof resizeModal === 'function') {
      resizeModal();
    }
  }, [showGuidancePanel, resizeModal]);

  const toggleShowGuidancePanel = () => {
    setShowGuidancePanel(!showGuidancePanel);
  };

  const handleClose = () => {
    Segment.event('Report click cancel', { VID: venueId, RID: reservationId });
    dispatch(closeModal());
  };

  const wait = ms => new Promise(resolve => setTimeout(resolve, ms));

  async function onSubmitReport(formValues) {
    Segment.event('Click Submit Report', {
      VID: venueId,
      ReservationID: reservationId,
      Type: reportType,
    });
    setProcessingStep();
    const minDelayPromise = wait(PROCESSING_MIN_DISPLAY_MS);
    const { feedback, description } = buildFeedbackAndDescription(formValues, reportType);
    try {
      const submitPromise = submitReservationFeedback({ reservationId, feedback, description });
      const response = await Promise.all([minDelayPromise, submitPromise]).then(([, result]) => result);
      dispatch(refreshAppointmentsAfterReservationFeedback({ venueId, scheduleId }));
      dispatch(refreshRosterAfterReservationFeedback({ venueId, scheduleId }));
      setSuccessStep(response.isPaid);
    } catch (error) {
      onSubmitError();
    }
  }

  return (
    <>
      <When predicate={isMobile}>
        <CardSection flush className={element('mobile-header')}>
          <ReportDetailsHeader fullName={fullName} profileUrl={profileUrl} isMobile={isMobile} />
        </CardSection>
      </When>
      <CardSection flush className={element('content')}>
        <div className={classNames(block(), 'reservation-feedback-modal-v2__step2-wrapper')}>
          <div className={element('report-badge-wrapper')}>
            <GuidancePanel
              reasonType={reasonType}
              guidelines={guidelines}
              isMobile={isMobile}
              isOpen={showGuidancePanel}
              onToggle={toggleShowGuidancePanel}
              venueId={venueId}
              reservationId={reservationId}
            />
            <div className={element('report-section')}>
              <When predicate={!isMobile}>
                <ReportDetailsHeader fullName={fullName} profileUrl={profileUrl} isMobile={isMobile} />
              </When>
              <When predicate={!!showSubmitError}>
                <div className={element('submit-error-wrapper')}>
                  <Callout className={element('submit-error')} icon={<Isvg src={ICONS.alert} />} variant="error">
                    <Message
                      message={messages.submitError}
                      values={{
                        contactUs: text => (
                          <Button onClick={openDecagonChat} styled="inline-link" type={Button.TYPES.BUTTON}>
                            {text}
                          </Button>
                        ),
                      }}
                    />
                  </Callout>
                </div>
              </When>
              <Form
                id={FORM_NAME}
                className={element('report-form')}
                defaultValues={{ additionalDetails: '', disruptiveReason: null }}
                onSubmit={() => () => Promise.resolve()}>
                {({ handleSubmit }) => (
                  <ReportFormPanel
                    fullName={fullName}
                    profileUrl={profileUrl}
                    reasonType={reasonType}
                    isMobile={isMobile}
                    handleClose={handleClose}
                    handleSubmit={handleSubmit}
                    onSubmitReport={onSubmitReport}
                    formId={FORM_NAME}
                  />
                )}
              </Form>
            </div>
          </div>
        </div>
      </CardSection>
    </>
  );
};

ReportDetails.propTypes = {
  reasonType: intlMessage.isRequired,
  guidelines: PropTypes.arrayOf(
    PropTypes.shape({
      description: PropTypes.oneOfType([intlMessage, PropTypes.arrayOf(intlMessage)]).isRequired,
      descriptionFooter: intlMessage,
      icon: PropTypes.string.isRequired,
      title: intlMessage.isRequired,
    })
  ).isRequired,
  fullName: PropTypes.string.isRequired,
  profileUrl: PropTypes.string,
  venueId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  reservationId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  scheduleId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  reportType: PropTypes.oneOf(REPORT_TYPES).isRequired,
  setProcessingStep: PropTypes.func.isRequired,
  setSuccessStep: PropTypes.func.isRequired,
  resizeModal: PropTypes.func,
  showSubmitError: PropTypes.bool,
  onSubmitError: PropTypes.func,
};

export default ReportDetails;
